import React, { useState, useEffect } from "react";
import {
  Stepper,
  Step,
  StepLabel,
  Button,
  Card,
  Box,
  Typography,
  Stack,
  Alert,
  Fade,
  CircularProgress,
} from "@mui/material";
import { Formik, Form, useFormikContext } from "formik";
import {
  steps,
  initialValues,
  extractStepRequestData,
} from "../constants/ocpi.constant";
import { ocpiWizardValidationSchema } from "src/util/validationSchemas";
import CompanySelectionStep from "./CompanySelectionStep";
import PartnerInfoStep from "./PartnerInfoStep";
import TechnicalConfigStep from "./TechnicalConfigStep";
import CredentialExchangeStep from "./CredentialExchangeStep";
import PartnerVerificationStep from "./PartnerVerificationStep";
import ReviewStep from "./ReviewStep";
import { Loader } from "src/components/Loader";

import { useParams, useNavigate } from "react-router-dom";
import { useMutate, useFetchOnDemand } from "src/hooks/useFetch.hook";
import { onboardUrl } from "src/react-query/endPoints";
import { useDropdown } from "src/hooks/useDropdown.hook";

const OcpiOnboardingWizard = () => {
  const { data: ocpiRoles } = useDropdown("OcpiRole");
  const { data: ocpiVersions } = useDropdown("OcpiVersion");
  const [activeStep, setActiveStep] = useState(0);
  const [submitError, setSubmitError] = useState("");
  const [initialFormikValues, setInitialFormikValues] = useState(initialValues);
  const { mutateAsync, isLoading } = useMutate("savePartner");
  const { id } = useParams();
  const navigate = useNavigate();
  const formikContext = useFormikContext();
  const {
    fetchData,
    isLoading: partnerLoading,
    data,
  } = useFetchOnDemand("partner", true);
  const isLastStep = activeStep === steps.length - 1;

  const handleNext = async (formik) => {
    setSubmitError("");
    try {
      const formData = await ocpiWizardValidationSchema[activeStep].validate(
        formik.values,
        { abortEarly: false }
      );
      const extractedFormData = extractStepRequestData(formData, activeStep);
      if (![0, 4].includes(activeStep)) {
        const result = await mutateAsync({
          method: "PUT",
          body: {
            partner_id:
              initialFormikValues.partnerId || formik.values.partnerId,
            data: extractedFormData,
            step: activeStep,
          },
          url: onboardUrl,
        });
        if ([1000].includes(result.status_code)) {
          formik.setFieldValue("partnerId", result.data.partnerId, false);
          formik.setFieldValue("step", activeStep, false);
          navigate(`/ocpi/partners/onboard/${result.data.partnerId}`, { replace: true });
          setActiveStep((prev) => prev + 1);
        }
      } else {
        setActiveStep((prev) => prev + 1);
      }
    } catch (err) {
      console.log(err);
      if (err.inner && err.inner.length) {
        const errors = {};
        err.inner.forEach((e) => {
          errors[e.path] = e.message;
        });
        formik.setTouched(errors);
        formik.setErrors(errors);
      }
    }
  };

  const handleBack = () => {
    setSubmitError("");
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async (values, actions) => {
    setSubmitError("");
    try {
      const extractedFormData = extractStepRequestData(values, activeStep);
      const result = await mutateAsync({
        method: "PUT",
        body: {
          partner_id: id,
          data: extractedFormData,
          step: activeStep - 1,
        },
        url: onboardUrl,
      });
      if ([1000].includes(result.status_code)) {
        navigate("/ocpi/partners");
      }
    } catch (err) {
      console.log(err);
      setSubmitError("Submission failed. Please check the form.");
    }
  };

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  useEffect(() => {
    if (id && data) {
      // Filter API response to only include keys in Formik's initialValues
      const matchedData = Object.keys(initialValues).reduce((acc, key) => {
        if (data.hasOwnProperty(key)) {
          acc[key] = data[key];
        }
        return acc;
      }, {});
      setInitialFormikValues((prev) => ({ ...prev, ...matchedData }));
      setActiveStep(+matchedData.step + 1);
    } else {
      setActiveStep(0);
    }
  }, [id, data, formikContext]);

  return (
    <Card sx={{ margin: "32px auto", p: 3 }}>
      <Box sx={{ mb: 5 }}>
        <Typography
          variant="h4"
          align="left"
          sx={{ fontWeight: 700 }}
          gutterBottom
        >
          OCPI Onboarding Wizard
        </Typography>
        <Typography variant="subtitle1" align="left" color="text.secondary">
          Complete partner registration with enhanced verification and security
          (Preview Mode)
        </Typography>
      </Box>
      {!partnerLoading ? (
        <>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {steps.map((label, idx) => (
              <Step key={label}>
                <StepLabel
                  StepIconProps={{
                    ...(isLoading && activeStep === idx
                      ? { sx: { position: "relative" } }
                      : {}),
                    ...(isLoading && activeStep === idx
                      ? {
                          icon: (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <span style={{ opacity: 0 }}>{idx + 1}</span>
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: 0,
                                  right: 0,
                                  top: 0,
                                  bottom: 0,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <span
                                  className="MuiCircularProgress-root MuiCircularProgress-colorPrimary MuiCircularProgress-indeterminate"
                                  style={{ width: 24, height: 24 }}
                                >
                                  <svg
                                    className="MuiCircularProgress-svg"
                                    viewBox="22 22 44 44"
                                  >
                                    <circle
                                      className="MuiCircularProgress-circle MuiCircularProgress-circleIndeterminate"
                                      cx="44"
                                      cy="44"
                                      r="20.2"
                                      fill="none"
                                      strokeWidth="3.6"
                                    />
                                  </svg>
                                </span>
                              </Box>
                            </Box>
                          ),
                        }
                      : {}),
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
          <Formik
            initialValues={initialFormikValues}
            validationSchema={ocpiWizardValidationSchema[activeStep]}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={true}
            enableReinitialize
          >
            {(formik) => {
              return (
                <Form>
                  <Box minHeight={260}>
                    <Fade in timeout={400} key={activeStep}>
                      <Box>
                        {activeStep === 0 && (
                          <CompanySelectionStep
                            value={formik.values.companyId}
                            onChange={formik.handleChange}
                            error={
                              formik.touched.companyId &&
                              formik.errors.companyId
                            }
                          />
                        )}
                        {activeStep === 1 && (
                          <PartnerInfoStep
                            {...formik}
                            roles={ocpiRoles}
                            versions={ocpiVersions}
                          />
                        )}
                        {activeStep === 2 && (
                          <TechnicalConfigStep {...formik} />
                        )}
                        {activeStep === 3 && (
                          <CredentialExchangeStep
                            values={formik.values}
                            setFieldValue={formik.setFieldValue}
                            isSubmitting={formik.isSubmitting}
                          />
                        )}
                        {/* {activeStep === 4 && (
                          <PartnerVerificationStep {...formik} />
                        )} */}
                        {activeStep === 4 && (
                          <ReviewStep
                            values={formik.values}
                            roles={ocpiRoles}
                            versions={ocpiVersions}
                          />
                        )}
                        {submitError && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            {submitError}
                          </Alert>
                        )}
                      </Box>
                    </Fade>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="flex-end"
                    mt={3}
                  >
                    {activeStep > 0 && (
                      <Button
                        onClick={handleBack}
                        variant="outlined"
                        disabled={isLoading}
                      >
                        Back
                      </Button>
                    )}
                    {!isLastStep && (
                      <Button
                        variant="contained"
                        onClick={() => handleNext(formik)}
                        color="primary"
                        disabled={
                          isLoading ||
                          (activeStep === 3 &&
                            !formik.values.credentialExchangeStatus.completed)
                        }
                        endIcon={isLoading && <CircularProgress size={24} />}
                      >
                        Next
                      </Button>
                    )}
                    {isLastStep && (
                      <Button
                        variant="contained"
                        color="primary"
                        type="submit"
                        disabled={isLoading}
                      >
                        Activate
                      </Button>
                    )}
                  </Stack>
                </Form>
              );
            }}
          </Formik>
        </>
      ) : (
        <Loader />
      )}
    </Card>
  );
};

export default OcpiOnboardingWizard;
